/**
 * Label Items Vuex module for Portal Application
 * Controls the data and actions for printing labels
 * 
 * @module LabelItems
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date June 2023
 */

import portalapi from "@services/portalapi";
import { duplicateHandlingFunctions } from "@services/duplicatesService";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
    listItems: [],
    labelCartItems: [],
    priceTicketPrintOptions: [],
    shelfLabelPrintOptions: [],
    printLabelSequenceOptions: [],
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getLabelItemsList(st) {
        return st.listItems;
    },

    getLabelItemsCart(st) {
        return st.labelCartItems;
    },

    getPriceTicketPrintOptions(st) {
        return st.priceTicketPrintOptions;
    },

    getShelfLabelPrintOptions(st) {
        return st.shelfLabelPrintOptions;
    },

    getPrintLabelSequenceOptions(st) {
        return st.printLabelSequenceOptions;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },

    setLabelItemsList(st, payload) {
        st.listItems = payload;
    },

    setLabelItemsCart(st, payload) {
        st.labelCartItems = payload;
    },

    setPriceTicketPrintOptions(st, payload) {
        st.priceTicketPrintOptions = payload;
    },

    setShelfLabelPrintOptions(st, payload) {
        st.shelfLabelPrintOptions = payload;
    },

    setPrintLabelSequenceOptions(st, payload) {
        st.printLabelSequenceOptions = payload;
    },
};

const actions = {

    // Get Label items
    async fetchLabelItemsList({ commit }, payload) 
    {
        const requestPayload = {
            page: (payload && payload.page ? payload.page : 1),
            itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 20),
            filters: (payload && payload.filters ? payload.filters : []),
            sortBy: (payload && payload.sortBy ? payload.sortBy : {column: "itemNumber", state: 0}),
        };

        const response = await portalapi.moduleItemLabels.getItemLabelListData(requestPayload);

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setLabelItemsList", response.data);
        }
    },


    // Get Label cart items
    async fetchLabelItemsCart({ commit }) 
    {
        const response = await portalapi.moduleItemLabels.getItemLabelCartData();

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            response.data.items.sort(duplicateHandlingFunctions.compareListItems);
            
            commit("setLabelItemsCart", response.data);
        }
    },

    
    // Get Price Ticket Print Options
    async fetchPriceTicketPrintOptions(context) 
    {
        var commit = context.commit;

        if (context.state.priceTicketPrintOptions.length == 0) {
            const response = await portalapi.moduleItemLabels.getPriceTicketPrintOptions();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setPriceTicketPrintOptions", response.data);
            }
        }
    },


    // Get Shelf Label Print Options
    async fetchShelfLabelPrintOptions(context) 
    {
        var commit = context.commit;

        if (context.state.shelfLabelPrintOptions.length == 0) {
            const response = await portalapi.moduleItemLabels.getShelfLabelPrintOptions();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setShelfLabelPrintOptions", response.data);
            }
        }
    },


    // Get Print Label Sequence Options
    async fetchPrintLabelSequenceOptions(context) 
    {
        var commit = context.commit;

        if (context.state.printLabelSequenceOptions.length == 0) {
            const response = await portalapi.moduleItemLabels.getPrintLabelSequenceOptions();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setPrintLabelSequenceOptions", response.data);
            }
        }
    },


    // Update Label cart data
    async updateLabelItemsCartData({ commit }, cartData) 
    {
        commit("setLabelItemsCart", cartData);
    }
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
